import React from "react";
import {
  Page,
  Text,
  View,
  Image,
  StyleSheet,
  Document,
  pdf,
} from "@react-pdf/renderer";
import { isLineDateOnly } from "../../utils/appointmentTimes";

// ---------- Helpers ----------
// Support both shapes: {caseAuthNumber} or {authorizationNumber}, etc.
const normalizeCaseInfo = (invoice) => {
  const c = invoice?.case || invoice?.inPatientCase || {};
  return {
    authNumber:
      c.caseAuthNumber || c.authorizationNumber || null,
    referringDoctor:
      c.referringDoctorName || c.caseReferringDoctor || null,
    referringBHF:
      c.referringDoctorBHF || c.caseBHF || null,
    referringHPCSA:
      c.referringDoctorHPCSA || c.caseHPCSA || null,
  };
};


const money = (x) => `R ${Number(x ?? 0).toFixed(2)}`;

// Look up PMB auth number for an ICD10 code from the invoice-level icd10Codes array
const formatIcdWithPmb = (code, icd10Codes) => {
  if (!code || !icd10Codes?.length) return code || "";
  const match = icd10Codes.find(
    (c) => c.ICD10_Code === code && c.isPMB && c.pmbAuthNo
  );
  return match ? `${code} PMB (Auth: ${match.pmbAuthNo})` : code;
};

// Accepts string | object | array of either
const formatModifiers = (modifier) => {
  if (modifier == null) return "N/A";
  const arr = Array.isArray(modifier) ? modifier : [modifier];

  const codes = arr
    .map((m) => {
      if (m == null) return null;
      if (typeof m === "string" || typeof m === "number") return String(m);
      if (typeof m === "object") return m.code ?? null;
      return null;
    })
    .filter(Boolean);

  return codes.length ? codes.join(", ") : "N/A";
};

// Extract "HH:mm" from an ISO-ish string, or fall back to last 5 chars if needed
const extractTimeHHmm = (value) => {
  if (!value) return "";
  if (typeof value === "string") {
    const d = new Date(value);
    if (!isNaN(d)) {
      const hh = String(d.getHours()).padStart(2, "0");
      const mm = String(d.getMinutes()).padStart(2, "0");
      return `${hh}:${mm}`;
    }
    const m = value.match(/(\d{2}:\d{2})$/);
    if (m) return m[1];
    return value.slice(-5);
  }
  return "";
};

// Extract "YYYY-MM-DD" from an ISO-ish string, or pass through a leading date.
const extractDateYYYYMMDD = (value) => {
  if (!value) return "";
  if (typeof value === "string") {
    const d = new Date(value);
    if (!isNaN(d)) {
      const y = d.getFullYear();
      const mo = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      return `${y}-${mo}-${day}`;
    }
    const m = value.match(/^(\d{4}-\d{2}-\d{2})/);
    if (m) return m[1];
  }
  return "";
};

// Column widths (keep header and rows in sync). MOD sits flush against PAT
// (narrow + right-aligned) so the modifier reads as part of the amount cluster
// rather than floating in the middle of the description column.
const COL_DESC = "58%";
const COL_MOD  = "8%";
const COL_PAT  = "12%";
const COL_MA   = "12%";
const COL_TOT  = "10%";
const COL_NUM  = "10%";

const styles = StyleSheet.create({
  page: { padding: 20, fontSize: 9 },
  logo: { height: 75, width: 150, marginBottom: 10 },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 10,
  },
  practiceName: {
    fontSize: 12,
    fontWeight: "bold",
    marginBottom: 4,
  },
  title: {
    textAlign: "center",
    fontSize: 14,
    fontWeight: "bold",
    marginVertical: 8,
    textTransform: "uppercase",
  },
  section: { marginBottom: 6 },
  line: { height: 1, backgroundColor: "#000", marginVertical: 6 },

  tableHeader: {
    flexDirection: "row",
    borderBottom: 1,
    fontWeight: "bold",
    paddingBottom: 2,
    marginTop: 6,
    fontSize: 9,
  },
  hDesc: { width: COL_DESC, paddingRight: 4 },
  hMod:  { width: COL_MOD,  textAlign: "right", paddingRight: 4 },
  hNum:  { width: COL_NUM,  textAlign: "right" },

  row:  { flexDirection: "row", marginVertical: 2 },
  cDesc: { width: COL_DESC, paddingRight: 4 },
  cMod:  { width: COL_MOD,  textAlign: "right", paddingRight: 4 },
  cNum:  { width: COL_NUM,  textAlign: "right" },

  bold: { fontWeight: "bold" },

  totalsWrap: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginTop: 4,
  },
  totalsLabels: { width: "25%" },
  totalsValues: { width: "15%" },

  // ── Per-line typography (mirrors statementPDF.js rhythm) ──
  // Patient header sits at 8pt bold with a small top gap. Each line below it
  // uses a 7pt bold date on its own row, a 7pt indented description, and a
  // 6pt grey ICD caption — tight 1–2 pt vertical spacing keeps the line
  // dense like the statement.
  patientHeader: {
    marginTop: 6,
    marginBottom: 2,
    fontWeight: "bold",
    fontSize: 9,
  },
  lineGroup:       { marginBottom: 3 },
  lineDate:        { fontSize: 8, fontWeight: "bold", marginBottom: 1 },
  lineRow:         { flexDirection: "row" },
  lineDescription: { fontSize: 8 },
  lineIcd:         { fontSize: 7, color: "#555", marginTop: 1 },
  lineMeta:        { fontSize: 8 },

  italics: { fontStyle: "italic" },

  metaBlock: { marginTop: 6, marginBottom: 6 },
  metaLine:  { fontSize: 8, lineHeight: 1.25 }, // small & uniform


});

const InvoicePDF = ({ invoice }) => {
  const {
    account,
    practice,
    invoiceNumber,
    invoiceDate,
    lines = [],
    totalAmount = 0,
    totalPatientAmount = 0,
    totalMedicalAidAmount = 0,
    practitioner = {},
    medicalAid = {},
    placeOfService,
    case: invoiceCase,
  } = invoice;

  const isVatRegistered = !!practice?.vatNumber;
  const vatAmount = isVatRegistered ? Number(totalAmount) - Number(totalAmount) / 1.15 : 0;
  const titleText = isVatRegistered ? "Tax Invoice" : "Invoice";
  const caseInfo = normalizeCaseInfo(invoice);

  // Discount breakdown per target — used to render the gross / discount /
  // net stack alongside the screen totals. Discount lines carry a positive
  // `discountAmount` and a `discountTarget` of "patient" or "medicalAid".
  const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
  let discountMedAid = 0;
  let discountPatient = 0;
  (lines || []).forEach((l) => {
    if (l?.type !== "discount") return;
    const amt = Math.abs(Number(l.discountAmount ?? l.rate ?? 0) || 0);
    if (l.discountTarget === "medicalAid") discountMedAid += amt;
    else discountPatient += amt;
  });
  discountMedAid = round2(discountMedAid);
  discountPatient = round2(discountPatient);
  const grossMedAid = round2(Number(totalMedicalAidAmount || 0) + discountMedAid);
  const grossPatient = round2(Number(totalPatientAmount || 0) + discountPatient);
// Build a single compact line like:
// "The patient was referred by Dr Jane (BHF: 123, HPCSA: 456) | Authorization 123456 applies"
const buildRefLine = (caseInfo) => {
  if (!caseInfo) return "";
  const bits = [];

  if (caseInfo.referringDoctor) {
    const regs = [];
    if (caseInfo.referringBHF) regs.push(`BHF: ${caseInfo.referringBHF}`);
    if (caseInfo.referringHPCSA) regs.push(`HPCSA: ${caseInfo.referringHPCSA}`);

    let by = `The patient was referred by ${caseInfo.referringDoctor}`;
    if (regs.length) by += ` (${regs.join(", ")})`;
    bits.push(by);
  }

  if (caseInfo.authNumber) {
    bits.push(`Authorization ${caseInfo.authNumber} applies`);
  }

  return bits.join(" | ");
};


  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Header */}
        <View style={styles.header}>
          {!!practice?.logoURL && (
            <Image
              style={[styles.logo, { maxHeight: 150, width: "auto" }]}
              src={practice.logoURL}
            />
          )}
          <View>
            <Text style={styles.practiceName}>{practice?.practiceName}</Text>

            <Text>
              <Text style={styles.bold}>Practice No: </Text>
              {practice?.practiceNumber}
            </Text>
            <Text>
              <Text style={styles.bold}>HPCSA No: </Text>
              {practice?.hpcsaNumber}
            </Text>

            {isVatRegistered && (
              <Text>
                <Text style={styles.bold}>VAT: </Text>
                {practice?.vatNumber}
              </Text>
            )}

            <View style={styles.line} />

            <Text>
              {practice?.practiceAddress} {practice?.practiceAddress2}
            </Text>
            <Text>
              {practice?.practiceCity} {practice?.practicePostalCode}
            </Text>

            <View
              style={[
                styles.section,
                { flexDirection: "row", justifyContent: "space-between" },
              ]}
            />

            <Text>
              <Text style={styles.bold}>Email: </Text>
              {practice?.practiceEmail}
            </Text>
            <Text>
              <Text style={styles.bold}>Tel: </Text>
              {practice?.practicePhoneNumber}
            </Text>
          </View>
        </View>

        {/* Title */}
        <Text style={styles.title}>{titleText}</Text>
        <Text style={styles.line} />

        {/* Invoice Info */}
        <View
          style={[
            styles.section,
            { flexDirection: "row", justifyContent: "space-between" },
          ]}
        >
          <Text style={styles.bold}>Invoice #: {invoiceNumber}</Text>
          <Text>Date: {invoiceDate}</Text>
        </View>

        <View
          style={[
            styles.section,
            { flexDirection: "row", justifyContent: "space-between" },
          ]}
        >
          {/* Left: Account */}
          <View style={{ width: "60%" }}>
            <Text style={styles.bold}>Billed To:</Text>
            <Text>
              {account?.accountFullNames || account?.accountName || ""}{" "}
              {account?.accountLastName || ""}
            </Text>
            <Text>ACC #: {account?.accountNumber || ""}</Text>
            <Text>
              {account?.accountHomeAddress1 || ""}{" "}
              {account?.accountHomeAddress2 || ""}
            </Text>
            <Text>
              {account?.accountHomeAddressCity || ""}{" "}
              {account?.accountHomeAddressPostalCode || ""}
            </Text>

            <View
              style={[
                styles.section,
                { flexDirection: "row", justifyContent: "space-between" },
              ]}
            />

            <Text>
              <Text style={styles.bold}>Tel: </Text>
              {account?.accountTelNumberCell || ""}
            </Text>
            <Text>
              <Text style={styles.bold}>Email: </Text>
              {account?.accountEmail || ""}
            </Text>
          </View>

          {/* Right: Medical Aid */}
         <View style={{ width: "40%", alignItems: "flex-end" }}>
  {medicalAid?.name ? (
    <>
      <Text style={styles.bold}>Medical Aid</Text>
      <Text>{medicalAid.name}</Text>
      <Text>{medicalAid.number || ""}</Text>
      <Text>
        {medicalAid.option || ""}
        {medicalAid.option && medicalAid.plan && " | "}
        {medicalAid.plan || ""}
      </Text>
    </>
  ) : account?.accountIODFund ? (
    <>
      <Text style={styles.bold}>IOD</Text>

      <Text>{account.accountIODFund}</Text>

      {(account.accountIODPlan || account.accountIODOption) && (
        <Text>
          {account.accountIODPlan || ""}
          {account.accountIODPlan && account.accountIODOption && " | "}
          {account.accountIODOption || ""}
        </Text>
      )}

      {account.accountIODClaimRefNumber && (
        <Text>
          <Text style={styles.bold}>Claim Ref: </Text>
          <Text>{account.accountIODClaimRefNumber}</Text>
        </Text>
      )}

      {account.accountIODEmployer && (
        <Text>
          <Text style={styles.bold}>Employer: </Text>
          <Text>{account.accountIODEmployer}</Text>
        </Text>
      )}

      {account.accountIODEmployerWCARegNum && (
        <Text>
          <Text style={styles.bold}>WCA Reg #: </Text>
          <Text>{account.accountIODEmployerWCARegNum}</Text>
        </Text>
      )}
      {account.accountIODDateOfInjury && (
        <Text>
          <Text style={styles.bold}>Date of Injury: </Text>
          <Text>{account.accountIODDateOfInjury}</Text>
        </Text>
      )}

      {/* ✅ Spacer so it doesn't feel cramped */}
      <View style={{ height: 6 }} />
    </>
  ) : (
    <>
      <Text style={styles.bold}>Private</Text>
      <Text>Private Patient</Text>
    </>
  )}
</View>

        </View>

        {/* Headings */}
        <View style={styles.tableHeader}>
          <Text style={{ width: COL_DESC, paddingRight: 4 }}>
            Tarrif | Nappi - Description
          </Text>
          <Text style={{ width: COL_MOD, textAlign: "right", paddingRight: 4 }}>Modifier</Text>
          <Text style={{ width: COL_PAT, textAlign: "right" }}>Patient</Text>
          <Text style={{ width: COL_MA,  textAlign: "right" }}>Medical Aid</Text>
          <Text style={{ width: COL_TOT, textAlign: "right" }}>Total</Text>
        </View>

        {/* Case info + Practitioner. The referral/authorization sentence is
            always confined to the description-column width so it wraps within
            that column instead of running the full page. Practitioner sits on
            its own line below. */}
        {(() => {
          const hasRef  = !!caseInfo.referringDoctor;
          const hasAuth = !!caseInfo.authNumber;
          const refLine = (hasRef || hasAuth) ? buildRefLine(caseInfo) : "";
          const practitionerStr = `Practitioner: ${practitioner?.firstName || ""} ${practitioner?.lastName || ""} (HPCSA: ${practitioner?.hpcsa || ""}) | Place of Service: ${placeOfService || ""}`;

          return (
            <View style={styles.metaBlock}>
              {refLine ? (
                <Text style={[styles.metaLine, { width: COL_DESC }]}>{refLine}</Text>
              ) : null}
              <Text style={styles.metaLine}>{practitionerStr}</Text>
            </View>
          );
        })()}

        {/* Lines grouped by patient */}
        {Object.values(
          lines.reduce((acc, line) => {
            const patientId =
              line?.patient?.patientId ||
              invoice?.patient?.patientId ||
              "unknown";
            if (!acc[patientId]) acc[patientId] = [];
            acc[patientId].push(line);
            return acc;
          }, {})
        ).map((patientLines, i) => {
          const firstLine = patientLines[0];
          const patient = firstLine?.patient || invoice?.patient;

          return (
            <View key={i} style={{ marginBottom: 6 }}>
              <Text style={styles.patientHeader}>
                ({patient?.patientMedicalAidDependantCode || "N/A"}){" "}
                {patient?.patientFirstName || patient?.patientFullNames || ""}{" "}
                {patient?.patientLastName || ""}
                {patient?.patientDOB ? ` (DOB: ${patient.patientDOB})` : ""}
              </Text>

              {patientLines.map((line, idx) => {
                // Per-line description wins — when a service is used, every
                // tariff line points to the same selectedService whose
                // `servicesDescription` is the umbrella label (e.g. "AX
                // General"), so picking that first collapsed every line to
                // the same text. `line.description` is set per-tariff at
                // invoice-creation time and matches PostedInvoice.jsx
                // (line 1879). selectedTariff.description and the service
                // description remain as fallbacks for edge cases.
                const descText =
                  line?.description ||
                  line?.selectedTariff?.description ||
                  line?.selectedService?.servicesDescription ||
                  "";

                // Build date + start–end time. Date-only lines omit the time.
                // When the appointment spans midnight (start date ≠ end date)
                // both dates are rendered, e.g. "2026-06-02 22:00 - 2026-06-03 01:00".
                const fallbackDate =
                  line?.appointmentDateSet ||
                  line?.appointmentDateCustom ||
                  "";
                const dateOnly = isLineDateOnly(line);
                const startISO = line?.appointmentStartSet || line?.appointmentCustomStart || "";
                const endISO = line?.appointmentEndSet || line?.appointmentCustomEnd || "";
                const startDate = extractDateYYYYMMDD(startISO) || fallbackDate;
                const endDate = extractDateYYYYMMDD(endISO) || fallbackDate;
                const startTime = dateOnly ? "" : extractTimeHHmm(startISO);
                const endTime = dateOnly ? "" : extractTimeHHmm(endISO);

                let dateWithTime;
                if (dateOnly) {
                  dateWithTime = fallbackDate || startDate;
                } else if (startDate && endDate && startDate !== endDate) {
                  const startPart = startTime ? `${startDate} ${startTime}` : startDate;
                  const endPart = endTime ? `${endDate} ${endTime}` : endDate;
                  dateWithTime = `${startPart} - ${endPart}`;
                } else {
                  const dateText = startDate || fallbackDate;
                  const timeRange = startTime
                    ? endTime
                      ? `${startTime} - ${endTime}`
                      : startTime
                    : "";
                  dateWithTime = timeRange ? `${dateText} ${timeRange}` : dateText;
                }

                const hasIcd = !!(line?.icd101 || line?.icd10);
                const icdText = hasIcd
                  ? `ICD 10: ${formatIcdWithPmb(line?.icd101 || line?.icd10, invoice.icd10Codes)}${
                      line?.icd102 ? `, ${formatIcdWithPmb(line.icd102, invoice.icd10Codes)}` : ""
                    }`
                  : "";

                // Stacked layout (matches statementPDF.js rhythm):
                //   • Date row — bold, on its own line, full-width.
                //   • Service row — description (indented) | modifier | amounts,
                //     amounts and modifier vertically aligned to the FIRST
                //     description line, ICD caption below in 6pt grey.
                return (
                  <View key={idx} style={styles.lineGroup}>
                    <Text style={styles.lineDate}>{dateWithTime}</Text>

                    <View style={styles.lineRow}>
                      <View style={{ width: COL_DESC, paddingLeft: 4, paddingRight: 4 }}>
                        <Text style={styles.lineDescription}>
                          {line?.tariffCode || line?.nappiCode
                            ? `${line.tariffCode || line.nappiCode}: `
                            : ""}
                          {descText}
                        </Text>
                        {hasIcd && (
                          <Text style={styles.lineIcd}>{icdText}</Text>
                        )}
                      </View>

                      {/* Modifiers (supports arrays & objects) */}
                      <View style={{ width: COL_MOD, textAlign: "right", paddingRight: 4 }}>
                        <Text style={styles.lineMeta}>{formatModifiers(line?.modifier)}</Text>
                      </View>

                      {/* Amounts */}
                      <View style={{ width: COL_PAT, textAlign: "right" }}>
                        <Text style={styles.lineMeta}>{money(line?.patientAmount)}</Text>
                      </View>
                      <View style={{ width: COL_MA, textAlign: "right" }}>
                        <Text style={styles.lineMeta}>{money(line?.medAidAmount)}</Text>
                      </View>
                      <View style={{ width: COL_TOT, textAlign: "right" }}>
                        <Text style={styles.lineMeta}>{money(line?.rate)}</Text>
                      </View>
                    </View>
                  </View>
                );
              })}
            </View>
          );
        })}

        <Text style={styles.line} />

        {/* Totals */}
        <View style={styles.totalsWrap}>
          {/* spacer */}
          <View style={{ width: "60%" }} />

          {/* labels */}
          <View style={styles.totalsLabels}>
            {discountMedAid > 0 && (
              <>
                <Text style={{ fontSize: 9, textAlign: "right" }}>
                  Medical Aid:
                </Text>
                <Text style={{ fontSize: 9, textAlign: "right" }}>
                  Medical Aid Discount:
                </Text>
              </>
            )}
            <Text
              style={{ fontSize: 9, fontWeight: "bold", textAlign: "right" }}
            >
              Total Medical Aid:
            </Text>
            <View
              style={{
                height: 1,
                backgroundColor: "#ccc",
                marginVertical: 4,
              }}
            />
            {discountPatient > 0 && (
              <>
                <Text style={{ fontSize: 9, textAlign: "right" }}>
                  Patient:
                </Text>
                <Text style={{ fontSize: 9, textAlign: "right" }}>
                  Patient Discount:
                </Text>
              </>
            )}
            <Text
              style={{ fontSize: 9, fontWeight: "bold", textAlign: "right" }}
            >
              Total Patient:
            </Text>
            <View
              style={{
                height: 1,
                backgroundColor: "#ccc",
                marginVertical: 4,
              }}
            />
            {isVatRegistered && (
              <Text style={{ fontSize: 9, textAlign: "right" }}>
                VAT Incl (15%):
              </Text>
            )}
            <View
              style={{ height: 1, backgroundColor: "#000", marginVertical: 6 }}
            />
            <Text
              style={{ fontSize: 9, fontWeight: "bold", textAlign: "right" }}
            >
              Invoice Total:
            </Text>
          </View>

          {/* values */}
          <View style={styles.totalsValues}>
            {discountMedAid > 0 && (
              <>
                <Text style={{ fontSize: 9, textAlign: "right" }}>
                  {money(grossMedAid)}
                </Text>
                <Text style={{ fontSize: 9, textAlign: "right" }}>
                  ({money(discountMedAid)})
                </Text>
              </>
            )}
            <Text
              style={{ fontSize: 9, fontWeight: "bold", textAlign: "right" }}
            >
              {money(totalMedicalAidAmount)}
            </Text>
            <View
              style={{
                height: 1,
                backgroundColor: "#ccc",
                marginVertical: 4,
              }}
            />
            {discountPatient > 0 && (
              <>
                <Text style={{ fontSize: 9, textAlign: "right" }}>
                  {money(grossPatient)}
                </Text>
                <Text style={{ fontSize: 9, textAlign: "right" }}>
                  ({money(discountPatient)})
                </Text>
              </>
            )}
            <Text
              style={{ fontSize: 9, fontWeight: "bold", textAlign: "right" }}
            >
              {money(totalPatientAmount)}
            </Text>
            <View
              style={{
                height: 1,
                backgroundColor: "#ccc",
                marginVertical: 4,
              }}
            />
            {isVatRegistered && (
              <Text style={{ fontSize: 9, textAlign: "right" }}>
                {money(vatAmount)}
              </Text>
            )}
            <View
              style={{ height: 1, backgroundColor: "#000", marginVertical: 6 }}
            />
            <Text
              style={{ fontSize: 9, fontWeight: "bold", textAlign: "right" }}
            >
              {money(totalAmount)}
            </Text>
          </View>
        </View>

        {/* Banking box */}
        <View
          style={{
            marginTop: 20,
            borderWidth: 1,
            borderColor: "#000",
            padding: 8,
            borderRadius: 4,
          }}
        >
          <Text style={{ fontSize: 8 }}>
            The patient outstanding amount can be paid directly into the
            following bank account. Please use your account number:{" "}
            {invoice?.account?.accountNumber || "N/A"} as the reference:
          </Text>

          <View style={{ marginTop: 10, width: "60%", paddingLeft: 6 }}>
            {[
              { label: "Bank", value: invoice?.banking?.[0]?.bank?.bankName },
              {
                label: "Account Name",
                value: invoice?.banking?.[0]?.bank?.accountName,
              },
              {
                label: "Account Number",
                value: invoice?.banking?.[0]?.bank?.accountNumber,
              },
              {
                label: "Branch Code",
                value: invoice?.banking?.[0]?.bank?.branchCode,
              },
              {
                label: "Account Type",
                value: invoice?.banking?.[0]?.bank?.accountType,
              },
            ].map((item, idx) => (
              <View key={idx} style={{ flexDirection: "row", marginBottom: 2 }}>
                <Text style={{ width: "40%", fontWeight: "bold", fontSize: 8 }}>
                  {item.label}:
                </Text>
                <Text style={{ width: "60%", fontSize: 8 }}>{item.value || "N/A"}</Text>
              </View>
            ))}
          </View>
        </View>
      </Page>
    </Document>
  );
};

// Exported PDF generator function
export async function ExportPDFInvoice(invoice, { asBlob = false } = {}) {
  try {
    const blob = await pdf(<InvoicePDF invoice={invoice} />).toBlob();

    if (asBlob) return blob;

    const blobURL = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = blobURL;
    link.download = `Invoice_${invoice?.invoiceNumber || "Untitled"}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(blobURL);
  } catch (error) {
    console.error("Error generating invoice PDF:", error);
  }
}
